import { useEffect, useState } from "react";
import { PencilIcon, TrashIcon, PlusIcon } from "@heroicons/react/outline";
import PageLoader from "../../components/pageLoader";
import Toast from "../../components/toast";
import ToastContainer from "../../components/toastContainer";
import {
  getResidencies,
  createResidency,
  updateResidency,
  deleteResidency,
} from "../../api/residenciesApi";
import ResidencyFormModal from "./residencyFormModal";

const ResidenciesPage = () => {
  const LIMIT = 10;

  const [residencies, setResidencies] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState(null);

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingResidency, setEditingResidency] = useState(null);
  const [saving, setSaving] = useState(false);

  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const totalPages = Math.ceil(total / LIMIT);

  const statusStyle = (status) => {
    switch (status) {
      case "active":
        return "text-emerald-400 bg-emerald-500/10 border-emerald-500/20";
      case "draft":
        return "text-amber-400 bg-amber-500/10 border-amber-500/20";
      case "finished":
        return "text-slate-400 bg-slate-500/10 border-slate-500/20";
      default:
        return "text-slate-400 bg-slate-500/10 border-slate-500/20";
    }
  };

  const statusLabel = (status) => {
    switch (status) {
      case "active":
        return "Active";
      case "draft":
        return "Draft";
      case "finished":
        return "Finished";
      default:
        return status || "—";
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const getPageNumbers = () => {
    const delta = 2;
    const range = [];
    const left = Math.max(2, page - delta);
    const right = Math.min(totalPages - 1, page + delta);
    range.push(1);
    if (left > 2) range.push("...");
    for (let i = left; i <= right; i++) range.push(i);
    if (right < totalPages - 1) range.push("...");
    if (totalPages > 1) range.push(totalPages);
    return range;
  };

  // ── Fetch ──
  const fetchResidencies = async () => {
    try {
      setLoading(true);
      setError("");
      const offset = (page - 1) * LIMIT;
      const data = await getResidencies(LIMIT, offset);
      setResidencies(data.results || []);
      setTotal(data.count || 0);
    } catch (err) {
      setError("Failed to load residencies.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchResidencies();
  }, [page]);

  // ── Create / Edit ──
  const handleSubmit = async (form) => {
    try {
      setSaving(true);

      const formData = new FormData();
      formData.append("name", form.name);
      formData.append("status", form.status);
      if (form.flyer && typeof form.flyer !== "string") {
        formData.append("flyer", form.flyer);
      }

      if (editingResidency) {
        await updateResidency(editingResidency.id, formData);
        setToast({
          type: "success",
          message: "Residency updated successfully.",
        });
      } else {
        await createResidency(formData);
        setToast({
          type: "success",
          message: "Residency created successfully.",
        });
      }

      setEditingResidency(null);
      setShowAddModal(false);
      fetchResidencies();
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail || err?.detail || "Something went wrong.",
      });
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ──
  const handleDelete = async () => {
    try {
      setDeleting(true);
      await deleteResidency(deleteId);
      setDeleteId(null);
      setToast({ type: "success", message: "Residency deleted." });
      fetchResidencies();
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail || err?.detail || "Failed to delete.",
      });
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <PageLoader />;

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white">Residencies</h1>
          <p className="text-sm text-slate-400">
            Create & manage residencies info.
          </p>
        </div>
        <button
          onClick={() => {
            setEditingResidency(null);
            setShowAddModal(true);
          }}
          className="flex items-center gap-2 bg-pink-600 hover:bg-pink-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition"
        >
          <PlusIcon className="w-4 h-4" />
          Create Residency
        </button>
      </div>

      {error && (
        <div className="px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-lg text-sm text-red-400">
          {error}
        </div>
      )}

      {/* ── Table ── */}
      <div className="bg-slate-900/60 border border-white/10 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table
            className="w-full border-collapse"
            style={{ minWidth: "700px" }}
          >
            <thead>
              <tr className="border-b border-white/10 text-xs uppercase tracking-wider text-slate-400">
                <th className="px-6 py-4">Name</th>
                <th className="px-6 py-4">Date Added</th>
                <th className="px-6 py-4">Landing Page</th>
                <th className="px-6 py-4">Flyer</th>
                <th className="px-6 py-4">Pixels</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>

            <tbody>
              {residencies.map((rs) => (
                <tr
                  key={rs.id}
                  className="border-b border-white/5 last:border-none hover:bg-slate-800/40 transition text-center"
                >
                  <td className="px-6 py-4 text-sm text-slate-400">
                    {rs.name}
                  </td>

                  <td className="px-6 py-4 text-sm text-slate-400">
                    {formatDate(rs.created_at || rs.updated_at)}
                  </td>

                  <td className="px-6 py-4 text-sm">
                    {rs.page_link ? (
                      <a
                        href={rs.page_link}
                        target="_blank"
                        rel="noreferrer"
                        className="text-pink-400 hover:text-pink-300 hover:underline transition"
                      >
                        View
                      </a>
                    ) : (
                      <span className="text-slate-600">—</span>
                    )}
                  </td>

                  <td className="px-6 py-4 text-sm text-slate-400">
                    {rs.flyer ? (
                      <a
                        href={rs.flyer}
                        target="_blank"
                        rel="noreferrer"
                        className="text-pink-400 hover:text-pink-300 hover:underline transition text-xs"
                      >
                        View
                      </a>
                    ) : (
                      <span className="text-slate-600">—</span>
                    )}
                  </td>

                  <td className="px-6 py-4 text-sm text-slate-400">
                    {"-"}
                  </td>

                  <td className="px-6 py-4">
                    <span
                      className={`px-2.5 py-0.5 rounded-md text-xs font-medium border ${statusStyle(rs.status)}`}
                    >
                      {statusLabel(rs.status)}
                    </span>
                  </td>

                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={() => {
                          setEditingResidency(rs);
                          setShowAddModal(false);
                        }}
                        className="p-2 rounded-md text-slate-400 hover:text-white hover:bg-slate-700 transition"
                        title="Edit"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setDeleteId(rs.id)}
                        className="p-2 rounded-md text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition"
                        title="Delete"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {residencies.length === 0 && (
          <div className="py-10 text-center text-slate-400 text-sm">
            No residencies found.
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-white/10">
            <p className="text-xs text-slate-500">
              Page {page} of {totalPages} · {total} results
            </p>
            <div className="flex items-center gap-1">
              <button
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
                className="px-3 py-1.5 rounded-lg border border-white/10 text-xs text-slate-300 hover:bg-slate-800 hover:text-white transition disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Previous
              </button>

              {getPageNumbers().map((pageNumber, idx) =>
                pageNumber === "..." ? (
                  <span key={idx} className="px-2 text-slate-600 text-xs">
                    …
                  </span>
                ) : (
                  <button
                    key={pageNumber}
                    onClick={() => setPage(pageNumber)}
                    className={`px-3 py-1.5 rounded-lg border text-xs transition ${
                      page === pageNumber
                        ? "bg-pink-600 border-pink-500 text-white"
                        : "border-white/10 text-slate-300 hover:bg-slate-800 hover:text-white"
                    }`}
                  >
                    {pageNumber}
                  </button>
                ),
              )}

              <button
                disabled={page === totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1.5 rounded-lg border border-white/10 text-xs text-slate-300 hover:bg-slate-800 hover:text-white transition disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Form Modal ── */}
      <ResidencyFormModal
        isOpen={showAddModal || !!editingResidency}
        onClose={() => {
          setShowAddModal(false);
          setEditingResidency(null);
        }}
        onSubmit={handleSubmit}
        loading={saving}
        mode={editingResidency ? "edit" : "create"}
        initialData={editingResidency}
      />

      {/* ── Delete Confirmation ── */}
      {!!deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="w-full max-w-sm bg-slate-900 border border-white/10 rounded-xl shadow-xl">
            <div className="px-6 py-5">
              <h2 className="text-white font-semibold text-sm mb-1">
                Delete Residency
              </h2>
              <p className="text-slate-400 text-sm">
                Are you sure you want to delete this residency? This action
                cannot be undone.
              </p>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-white/10">
              <button
                onClick={() => setDeleteId(null)}
                className="px-4 py-2 text-sm rounded-lg text-slate-300 hover:bg-slate-800 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 text-sm rounded-lg text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 transition flex items-center gap-2"
              >
                {deleting ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                    Deleting...
                  </>
                ) : (
                  "Delete"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer>
        {toast && (
          <Toast
            type={toast.type}
            message={toast.message}
            onClose={() => setToast(null)}
          />
        )}
      </ToastContainer>
    </div>
  );
};

export default ResidenciesPage;
